import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, RefreshCw, Database, ExternalLink, Network as NetworkIcon, TreePine, Info, Eye, EyeOff, FileText, Tag, Hash, Plus, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Network, Options } from "vis-network";
import { DataSet } from "vis-data";
import { useGetGraphData, useGetNodeWithNeighbors, useExpandNodeChildren, useGetRepoFiles, useGetProjectUserJourneys } from "@/service-hooks/use-get-graph-data";
import { useGetCodeOntologies } from "@/service-hooks/use-code-ontology-service";
import { transformGraphData, getLegendGroups, type LegendGroup } from "@/lib/graph-transforms";
import { useTheme } from "@/context/theme-context";
import { config } from "@/config";

interface Neo4jVisualizerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Render as a full-height page instead of a modal. The page route
   * (/knowledge-graph/:id) passes this; the citation "Node Focus View" peek
   * stays modal so it does not navigate the reader away from what they were on.
   */
  asPage?: boolean;
  focusNodeId?: string; // Optional node ID to focus on with neighbors
  // BREEZEAI-1136 DEF-01: caller-supplied label for the focus node. When
  // provided, the neighbor Cypher can go straight to a labelled index lookup
  // instead of scanning every node — dropping the query from ~50 s (which the
  // ingress converts to a 504 that Chromium reports as CORS) to single-digit
  // ms. Optional: when omitted, an indexed UNION resolve runs server-side.
  focusLabel?: string;
}

// Neo4j-style vis-network configuration with improved clustering
const getVisNetworkOptions = (isLightTheme: boolean): Options => ({
  nodes: {
    borderWidth: 3,
    borderWidthSelected: 6,
    chosen: {
      node: true,
      label: true,
    },
    scaling: {
      min: 20,
      max: 80,
      label: {
        enabled: true,
        min: 10,
        max: 16,
        maxVisible: 30,
        drawThreshold: 1,
      },
    },
    labelHighlightBold: false,
    font: {
      size: 12,
      color: isLightTheme ? "#000000" : "#ffffff",
      face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      strokeWidth: 0,
      strokeColor: "transparent",
    },
    shadow: false,
  },
  edges: {
    width: 2,
    scaling: {
      min: 1,
      max: 4,
      label: {
        enabled: true,
        min: 9,
        max: 12,
        maxVisible: 20,
        drawThreshold: 1,
      },
    },
    labelHighlightBold: false,
    font: {
      size: 11,
      color: isLightTheme ? "#000000" : "#333333",
      face: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.8)",
      strokeWidth: 0,
      align: "middle",
    } as any,
    arrows: {
      to: {
        enabled: true,
        scaleFactor: 1,
        type: "arrow",
      },
    },
    smooth: {
      enabled: true,
      type: "straightCross",
      roundness: 0.2,
    },
    shadow: false,
  },
  physics: {
    enabled: true,
    stabilization: {
      enabled: true,
      iterations: 300,
      updateInterval: 25,
      fit: true,
    },
    barnesHut: {
      gravitationalConstant: -4000,
      centralGravity: 0.1,
      springLength: 200,
      springConstant: 0.04,
      damping: 0.6,
      avoidOverlap: 0.8,
    },
    maxVelocity: 20,
    minVelocity: 0.75,
    timestep: 0.5,
  },
  interaction: {
    hover: true,
    hoverConnectedEdges: false,
    selectConnectedEdges: false,
    tooltipDelay: 200,
    zoomView: true,
    dragView: true,
  },
  layout: {
    improvedLayout: true,
    clusterThreshold: 150,
    hierarchical: {
      enabled: false,
      levelSeparation: 200,
      nodeSpacing: 150,
      treeSpacing: 200,
      blockShifting: true,
      edgeMinimization: true,
      parentCentralization: true,
      direction: 'UD',
      sortMethod: 'directed',
    },
  },
  groups: {
    project: {
      color: { background: "#68CCF1", border: "#4A9ECA" },
      shape: "dot",
      size: 60,
    },
    architectural: {
      color: { background: "#C990C0", border: "#B67FAC" },
      shape: "dot",
      size: 50,
    },
    functional: {
      color: { background: "#F16667", border: "#E85153" },
      shape: "dot",
      size: 50,
    },
    design: {
      color: { background: "#4C8EDA", border: "#3A7BC8" },
      shape: "dot",
      size: 50,
    },
    persona: {
      color: { background: "#FFB3E6", border: "#E694D1" },
      shape: "dot",
      size: 42,
    },
    outcome: {
      color: { background: "#A4DD00", border: "#8BC234" },
      shape: "dot",
      size: 36,
    },
    scenario: {
      color: { background: "#569480", border: "#4A7F6E" },
      shape: "dot",
      size: 32,
    },
    step: {
      color: { background: "#AD7BE9", border: "#9B6AD4" },
      shape: "dot",
      size: 28,
    },
    action: {
      color: { background: "#FB8500", border: "#D96D00" },
      shape: "dot",
      size: 24,
    },
    document: {
      color: { background: "#F79767", border: "#E8825A" },
      shape: "dot",
      size: 38,
    },
    // Architectural subcategories
    personalizedux: {
      color: { background: "#E6A3DC", border: "#D285C8" },
      shape: "dot",
      size: 38,
    },
    workflowservices: {
      color: { background: "#D4A3E6", border: "#C085D2" },
      shape: "dot",
      size: 38,
    },
    entitymicroservices: {
      color: { background: "#C2A3E6", border: "#AE85D2" },
      shape: "dot",
      size: 38,
    },
    externalintegrations: {
      color: { background: "#B0A3E6", border: "#9C85D2" },
      shape: "dot",
      size: 38,
    },
  },
});

// Legend content is derived from NODE_STYLES in graph-transforms, so it can
// never disagree with what is actually drawn. Shapes are reproduced here in CSS
// because shape now carries meaning alongside colour.
const LEGEND_GROUPS = getLegendGroups();

const SHAPE_CSS: Record<string, string> = {
  dot: '50%',
  square: '2px',
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  triangle: 'polygon(50% 0%, 100% 100%, 0% 100%)',
  triangleDown: 'polygon(0% 0%, 100% 0%, 50% 100%)',
  hexagon: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
  star: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
};

function LegendSwatch({ shape, color, border }: { shape: string; color: string; border: string }) {
  const css = SHAPE_CSS[shape];
  const isClip = css?.startsWith('polygon');
  return (
    <span
      className="w-4 h-4 flex-shrink-0 inline-block"
      aria-hidden="true"
      style={{
        backgroundColor: color,
        border: isClip ? undefined : `2px solid ${border}`,
        borderRadius: isClip ? undefined : css ?? '2px',
        clipPath: isClip ? css : undefined,
      }}
    />
  );
}

// Intelligent clustering and positioning algorithm  
const applyClusterPositioning = (nodes: any[], _edges: any[]) => {
  // Group nodes by their primary label/type
  const nodeGroups = new Map<string, any[]>();
  const rootNodes: any[] = [];
  
  nodes.forEach(node => {
    if (node.group === 'project') {
      rootNodes.push(node);
    } else {
      const group = node.group || 'unknown';
      if (!nodeGroups.has(group)) {
        nodeGroups.set(group, []);
      }
      nodeGroups.get(group)!.push(node);
    }
  });
  
  // Define hierarchy levels and their angular positions
  const hierarchyLevels = new Map([
    ['project', { level: 0, radius: 0, angleOffset: 0 }],
    ['architectural', { level: 1, radius: 3250, angleOffset: 270 }],
    ['functional', { level: 1, radius: 250, angleOffset: Math.PI * 2/3 }],
    ['design', { level: 1, radius: 3250, angleOffset: Math.PI * 4/3 }],
    // Architectural sub-nodes arranged in architectural sector (angleOffset: 0)
    ['personalizedux', { level: 2, radius: 400, angleOffset: 0 }],
    ['workflowservices', { level: 2, radius: 400, angleOffset: Math.PI * 0.5 }],
    ['entitymicroservices', { level: 2, radius: 400, angleOffset: Math.PI * 1.5 }],
    ['externalintegrations', { level: 2, radius: 400, angleOffset: Math.PI }],
    // Functional hierarchy (functional sector)
    ['persona', { level: 2, radius: 400, angleOffset: Math.PI * 2/3 }],
    ['document', { level: 2, radius: 400, angleOffset: Math.PI }],
    ['outcome', { level: 3, radius: 550, angleOffset: Math.PI * 2/3 }],
    ['scenario', { level: 4, radius: 700, angleOffset: Math.PI * 2/3 }],
    ['step', { level: 5, radius: 850, angleOffset: Math.PI * 2/3 }],
    ['action', { level: 6, radius: 1000, angleOffset: Math.PI * 2/3 }],
  ]);
  
  // Position root nodes at center
  rootNodes.forEach((node, index) => {
    node.x = index * 100 - (rootNodes.length - 1) * 50;
    node.y = 0;
    node.fixed = { x: false, y: false };
  });
  
  // Position other nodes in circular clusters around their hierarchy level
  nodeGroups.forEach((groupNodes, groupName) => {
    const hierarchy = hierarchyLevels.get(groupName) || { level: 3, radius: 600, angleOffset: 0 };
    const baseRadius = hierarchy.radius;
    const baseAngle = hierarchy.angleOffset;
    
    // Calculate positions for nodes in this group
    const nodeCount = groupNodes.length;
    const angleStep = nodeCount > 1 ? (Math.PI * 1.5) / Math.max(nodeCount - 1, 1) : 0;
    const startAngle = baseAngle - (angleStep * (nodeCount - 1)) / 2;
    
    groupNodes.forEach((node, index) => {
      // Add some spacing between similar nodes to prevent overlap
      const radiusVariation = Math.floor(index / 8) * 80; // Every 8 nodes, increase radius
      const currentRadius = baseRadius + radiusVariation;
      const currentAngle = startAngle + (angleStep * index);
      
      // Apply circular positioning
      node.x = Math.cos(currentAngle) * currentRadius;
      node.y = Math.sin(currentAngle) * currentRadius;
      
      // Add slight random offset to break perfect symmetry and prevent overlaps
      node.x += (Math.random() - 0.5) * 60;
      node.y += (Math.random() - 0.5) * 60;
      
      // Allow physics to fine-tune positions
      node.fixed = { x: false, y: false };
    });
  });
  
  return nodes;
};

/**
 * Outer frame for the visualizer. The body is ~400 lines and identical in both
 * presentations, so only the frame is swapped rather than duplicated.
 *
 * Declared at module level on purpose: a component defined inside the render
 * gets a fresh identity every render, which would unmount the vis-network
 * canvas and throw away the user's expanded graph on every state change.
 */
/**
 * Repositories live in PostgreSQL and have no node in Neo4j — Project has no
 * HAS_CODE edge, so no traversal can reach them. These two ids mark the nodes
 * the UI invents to hang them off, and they are the only nodes on the canvas
 * that are not real graph nodes.
 */
const CODE_ROOT_ID = 'synthetic:code';
const repoNodeId = (id: string | number) => `synthetic:repo:${id}`;

/** Labels with no range index on `id`; expanding from one would scan the DB. */
const LABELS_WITHOUT_ID_INDEX = new Set(['Statement']);

function VisualizerShell({
  asPage,
  isOpen,
  onClose,
  isLightTheme,
  children,
}: {
  asPage: boolean;
  isOpen: boolean;
  onClose: () => void;
  isLightTheme: boolean;
  children: ReactNode;
}) {
  if (asPage) {
    return (
      <div
        className={`flex h-full w-full flex-col overflow-hidden rounded-lg border ${
          isLightTheme ? '' : 'bg-surface border-surface-border'
        }`}
        style={{
          backgroundColor: isLightTheme ? '#ffffff' : undefined,
          borderColor: isLightTheme ? '#e2e8f0' : undefined,
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-6xl h-[85vh] p-0 overflow-hidden flex flex-col [&>button]:hidden ${
          isLightTheme
            ? ''
            : 'bg-surface border-surface-border'
        }`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{
          // Hide any default close buttons
          ['--radix-dialog-content-transform-origin' as any]: 'var(--radix-popper-transform-origin)',
          backgroundColor: isLightTheme ? '#ffffff' : undefined,
          borderColor: isLightTheme ? '#e2e8f0' : undefined,
        }}
      >
        <DialogTitle className="sr-only">Knowledge Graph Visualizer</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default function Neo4jVisualizer({ isOpen, onClose, focusNodeId, focusLabel, asPage = false }: Neo4jVisualizerProps) {
  const { id: projectUuid } = useParams(); // Get project UUID from URL when needed
  const { theme } = useTheme();
  // Consider light theme as the light variant, all others are dark variants
  const isLightTheme = theme === 'light';
  const vizContainerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [useHierarchical, setUseHierarchical] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [legendTab, setLegendTab] = useState<LegendGroup>('Functional');
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [selectedNodeMetadata, setSelectedNodeMetadata] = useState<any>(null);

  // The network is built over DataSets rather than plain arrays so a lazy
  // expansion can merge nodes in without destroying and recreating the Network —
  // recreating it would reset the camera and collapse everything the user had
  // already opened. Same approach as functional-tree-graph.
  const nodesDataSetRef = useRef<DataSet<any> | null>(null);
  const edgesDataSetRef = useRef<DataSet<any> | null>(null);
  // Node ids already expanded, so the panel can say so instead of re-querying.
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  // Every node currently on the canvas, keyed by id. Serves two things that both
  // broke when they read graphData instead: expansion needs the label to hit the
  // per-label index on `id`, and the details panel needs labels + properties.
  // Nodes added by an expansion never enter graphData, so neither could find them.
  // Child counts for the ontology roots, derived from the second level of the
  // root query. Only the roots have these; deeper nodes report their count by
  // being expanded.
  const rootChildCountsRef = useRef<Map<string, number>>(new Map());
  const nodeDataRef = useRef<
    Map<
      string,
      {
        id: string;
        labels: string[];
        properties: any;
        /** Set on the UI-invented nodes; they expand from PostgreSQL, not Cypher. */
        synthetic?: 'code-root' | 'repo' | 'design-root';
        codeOntologyId?: number;
      }
    >
  >(new Map());
  const [expandingNodeId, setExpandingNodeId] = useState<string | null>(null);

  // Generate a session ID when dialog opens to force fresh queries
  const [sessionId, setSessionId] = useState(0);
  
  // BREEZEAI-1136 TC-17 (DEF-01): when a `?focus=<nodeId>` deep-link fails
  // (CORS / 504 from a slow anchor resolve on the backend), the user was left
  // staring at "Loading knowledge graph..." indefinitely. On focus-query
  // failure we fall back to loading the full graph so the page is at least
  // usable, and toast a warning that the focus target could not be resolved.
  const [focusFailed, setFocusFailed] = useState(false);
  const useFocusMode = !!focusNodeId && !focusFailed;

  // Use focused node query if focusNodeId is provided, otherwise use full graph
  const { data: fullGraphData, isLoading: isLoadingFull, error: fullQueryError, isFetching: isFetchingFull } = useGetGraphData(projectUuid || '', isOpen && !useFocusMode && !!projectUuid, sessionId);
  const { data: nodeGraphData, isLoading: isLoadingNode, error: nodeQueryError, isFetching: isFetchingNode } = useGetNodeWithNeighbors(focusNodeId || '', isOpen && useFocusMode, sessionId, focusLabel);

  useEffect(() => {
    if (nodeQueryError && !focusFailed) {
      setFocusFailed(true);
      toast.warning(
        "Could not focus that node — showing the full graph instead.",
      );
    }
  }, [nodeQueryError, focusFailed]);

  // Select the appropriate data source
  const graphData = useFocusMode ? nodeGraphData : fullGraphData;
  const isLoading = useFocusMode ? isLoadingNode : isLoadingFull;
  const isFetching = useFocusMode ? isFetchingNode : isFetchingFull;
  const queryError = useFocusMode ? nodeQueryError : fullQueryError;
  const error = queryError?.message || null;

  // User journeys, for the same reason as the repository list: a few dozen at
  // most, and the count decides whether the Design root is worth showing at all.
  // Enabled once we're in full-graph mode — either because there was no focus
  // to begin with, or because the focus fell back (TC-17).
  const { data: journeyGraph, isLoading: isLoadingJourneys } = useGetProjectUserJourneys(
    projectUuid || '',
    isOpen && !useFocusMode
  );
  const journeys: any[] = journeyGraph?.nodes ?? [];

  
  // Track if we've initialized for this dialog session
  const [hasInitialized, setHasInitialized] = useState(false);
  

  // Initialize vis-network when data is available and dialog is open.
  //
  // Waits on the journey query as well as the root query. Whether the Design root
  // is drawn depends on the journey count, and that decision is made once here —
  // if the journeys landed after this ran, Design would be judged empty and
  // dropped on a project that actually has a design ontology. A disabled query
  // reports isLoading false, so the focus-node view is not held up by it.
  useEffect(() => {
    if (isOpen && graphData && !isLoadingJourneys && vizContainerRef.current && !hasInitialized) {
      initializeVisualization();
      setHasInitialized(true);
    }
  }, [isOpen, graphData, isLoadingJourneys, hasInitialized, useHierarchical, isLightTheme]);

  // Reset states when dialog opens/closes OR theme changes
  useEffect(() => {
    if (isOpen) {
      // Clean up any existing network
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }

      setIsConnected(false);
      setHasInitialized(false);
      setSelectedNodeMetadata(null);

      // Increment session ID to force a fresh query with new cache key
      setSessionId(prev => prev + 1);

    } else {
      // Reset when dialog closes
      setHasInitialized(false);
      setIsConnected(false);
      setShowLegend(true); // Reset legend to visible by default
      setLegendExpanded(false); // ...but collapsed, so it does not cover the graph
      setSelectedNodeMetadata(null);
    }
  }, [isOpen, isLightTheme]);

  // Cleanup network instance
  useEffect(() => {
    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }
    };
  }, []);

  // BREEZEAI-1136 TC-30 (DEF-03): vis-network mounts its own <canvas> inside
  // `vizContainerRef`. The wrapper div already carries role="img" + aria-label,
  // but screen readers walking the canvas element itself see nothing. Mirror
  // the same accessible name onto the canvas once vis-network has drawn it,
  // and keep the label in sync as the node count changes on expansion.
  useEffect(() => {
    if (!hasInitialized || !vizContainerRef.current) return;
    const canvas = vizContainerRef.current.querySelector("canvas");
    if (!canvas) return;
    canvas.setAttribute("role", "img");
    canvas.setAttribute(
      "aria-label",
      `Interactive knowledge graph with ${graphData?.nodes?.length ?? 0} nodes. Use mouse to pan and zoom, click nodes for details.`,
    );
    canvas.setAttribute("aria-describedby", "graph-instructions");
  }, [hasInitialized, graphData?.nodes?.length]);

  // Hide default dialog close buttons
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const dialogContent = document.querySelector('[data-radix-dialog-content]');
        if (dialogContent) {
          const buttons = dialogContent.querySelectorAll('button:not(.custom-close-btn)');
          buttons.forEach((button) => {
            const buttonElement = button as HTMLElement;
            const hasCloseIcon = buttonElement.querySelector('svg') && (
              buttonElement.textContent?.trim() === '' || 
              buttonElement.getAttribute('aria-label')?.toLowerCase().includes('close')
            );
            if (hasCloseIcon && !buttonElement.closest('.custom-close-btn')) {
              buttonElement.style.display = 'none';
            }
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const initializeVisualization = () => {
    if (!vizContainerRef.current || !graphData) {
      return;
    }

    try {
      // Clean up existing network
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }

      // Clear container
      vizContainerRef.current.innerHTML = '';

      // Two adjustments before the graph is drawn.
      //
      // Design is dropped when it came back childless. The root node always
      // exists, but until a design ontology is generated it leads nowhere, and a
      // permanently dead branch reads as a broken screen rather than an empty one.
      // It reappears by itself as soon as the design graph has content.
      //
      // Code is grafted on because it is missing for the opposite reason: the
      // repositories are real, but they are PostgreSQL rows with no node and no
      // edge in Neo4j, so nothing in the traversal can reach them.
      const rootEdges = graphData.edges ?? [];
      const projectNode = graphData.nodes.find((n: any) => n.labels?.includes('Project'));

      // The query returns two levels; only the first is drawn. The second level
      // is used to count each root's children, which is what lets the panel say
      // "Expand · 6 personas" and tells an empty Design apart from an unopened one.
      const childCounts = new Map<string, number>();
      rootEdges.forEach((e: any) => {
        const from = String(e.from);
        childCounts.set(from, (childCounts.get(from) ?? 0) + 1);
      });
      rootChildCountsRef.current = childCounts;

      const projectId = projectNode ? String(projectNode.id) : null;
      const isRootLevel = (n: any) =>
        String(n.id) === projectId ||
        rootEdges.some((e: any) => String(e.from) === projectId && String(e.to) === String(n.id));

      // Show every root-level child (Functional / Architectural / Design / Code)
      // regardless of whether its ontology has been generated yet, so the four
      // siblings are consistent and the user can click any empty one to go
      // generate it. expandDesignRoot() already handles the empty case with a
      // helpful toast, so leaving Design visible when empty doesn't break click.
      const keptNodes = graphData.nodes.filter((n: any) => isRootLevel(n));
      const keptIds = new Set(keptNodes.map((n: any) => String(n.id)));
      const keptEdges = rootEdges.filter(
        (e: any) => keptIds.has(String(e.from)) && keptIds.has(String(e.to))
      );

      const augmented = {
        nodes: [...keptNodes],
        edges: [...keptEdges],
      } as typeof graphData;

      // Projects created after BREEZEAI-533 (commit a893568) already get a real
      // :Code node from the backend's createWithSpecs. Injecting the synthetic
      // one on top rendered two "Code" nodes side-by-side. Only inject when the
      // backend didn't return a real :Code — legacy projects still rely on this
      // for the Postgres-backed repository list.
      const realCodeNode = keptNodes.find(
        (n: any) => n.labels?.includes('Code') && String(n.id) !== CODE_ROOT_ID
      );

      if (projectNode && !realCodeNode) {
        augmented.nodes.push({
          id: CODE_ROOT_ID,
          labels: ['Code'],
          properties: { name: 'Code', source: 'PostgreSQL' },
        } as any);
        augmented.edges.push({
          id: `${CODE_ROOT_ID}|edge`,
          from: String(projectNode.id),
          to: CODE_ROOT_ID,
          type: 'HAS_CODE',
          properties: {},
        } as any);
      }

      // Transform Neo4j data to vis-network format
      const { nodes, edges } = transformGraphData(augmented, isLightTheme);

      if (nodes.length === 0) {
        toast.error('No data found for this project');
        return;
      }

      // Configure layout options based on selected mode
      const layoutOptions = { ...getVisNetworkOptions(isLightTheme) };
      let finalNodes = nodes;

      if (useHierarchical) {
        // Enable hierarchical layout
        layoutOptions.layout = {
          ...layoutOptions.layout,
          hierarchical: {
            enabled: true,
            levelSeparation: 200,
            nodeSpacing: 150,
            treeSpacing: 200,
            blockShifting: true,
            edgeMinimization: true,
            parentCentralization: true,
            direction: 'UD',
            sortMethod: 'directed',
          },
        };
      } else {
        // Apply intelligent clustering and positioning to prevent overlaps
        finalNodes = applyClusterPositioning([...nodes], edges);
        layoutOptions.layout = {
          ...layoutOptions.layout,
          hierarchical: { enabled: false },
        };
      }

      // Create network with appropriate layout
      // A rebuild recreates the DataSets from the root query alone, so every
      // previous expansion is gone from the canvas. Drop the record of them too,
      // or the panel would offer "Expanded" for nodes no longer on screen.
      setExpandedNodeIds(new Set());
      setExpandingNodeId(null);
      nodeDataRef.current = new Map(
        augmented.nodes.map((n: any) => [
          String(n.id),
          {
            id: String(n.id),
            labels: n.labels ?? [],
            properties: n.properties ?? {},
            // Only the legacy synthetic Code node routes through expandCodeRoot
            // (Postgres repository list). When the backend returned a real :Code
            // node, let it use the same Cypher-based fetchChildren path as its
            // siblings (Functional / Architectural) so a freshly-uploaded code
            // ontology shows up without a cache round-trip — the backend's
            // createForCodeOntology already writes a :CodeOntologyNode child
            // under :Code on upload, so Cypher sees it immediately.
            synthetic:
              n.id === CODE_ROOT_ID
                ? ('code-root' as const)
                : n.labels?.includes('Design')
                ? ('design-root' as const)
                : undefined,
          },
        ])
      );

      nodesDataSetRef.current = new DataSet(finalNodes as any[]);
      edgesDataSetRef.current = new DataSet(edges as any[]);

      networkRef.current = new Network(
        vizContainerRef.current,
        { nodes: nodesDataSetRef.current, edges: edgesDataSetRef.current },
        layoutOptions
      );

      // Add event listeners
      networkRef.current.on('stabilized', () => {
        setIsConnected(true);
      });

      // Remove excessive logging that might affect performance
      // networkRef.current.on('configChange', () => {
      //   console.log('Network config changed');
      // });

      // networkRef.current.on('beforeDrawing', (ctx) => {
      //   console.log('Network before drawing');
      // });

      networkRef.current.on('selectNode', (event) => {
        try {
          if (event.nodes.length > 0) {
            const nodeId = String(event.nodes[0]);
            const originalNode = nodeDataRef.current.get(nodeId);

            if (originalNode) {
              setSelectedNodeMetadata({
                id: originalNode.id,
                labels: originalNode.labels,
                properties: originalNode.properties,
                nodeId,
              });
            }
          }
        } catch (error) {
          console.error('Error in selectNode handler:', error);
        }
      });

      // Double-click is the natural expand gesture; the panel button does the
      // same thing for anyone who does not discover it.
      networkRef.current.on('doubleClick', (event) => {
        if (event.nodes.length > 0) {
          handleExpandNode(String(event.nodes[0]));
        }
      });

      networkRef.current.on('deselectNode', () => {
        try {
          setSelectedNodeMetadata(null);
        } catch (error) {
          console.error('Error in deselectNode handler:', error);
        }
      });

      networkRef.current.on('selectEdge', (_event) => {
        try {
          // Handle edge selection if needed
        } catch (error) {
          console.error('Error in selectEdge handler:', error);
        }
      });

      // Keep essential event handlers
      networkRef.current.on('stabilizationIterationsDone' as any, () => {
        // Network stabilization complete
      });

      // networkRef.current.on('animationFinished', () => {
      //   console.log('Network animation finished');
      // });

      // Add a failsafe to redraw if the canvas becomes blank
      networkRef.current.on('afterDrawing', (ctx) => {
        // Check if canvas is actually drawing something
        const canvas = ctx.canvas;
        if (canvas && canvas.width > 0 && canvas.height > 0) {
          // Canvas seems fine
        } else {
          setTimeout(() => {
            if (networkRef.current) {
              try {
                networkRef.current.redraw();
              } catch (error) {
              }
            }
          }, 100);
        }
      });

      // Set initial connected state
      setIsConnected(true);

    } catch (err) {
      toast.error('Failed to initialize graph visualization');
      setIsConnected(false);
    }
  };

  const handleRefresh = () => {
    // Clean up existing network first
    if (networkRef.current) {
      networkRef.current.destroy();
      networkRef.current = null;
    }
    
    // Clear container
    if (vizContainerRef.current) {
      vizContainerRef.current.innerHTML = '';
    }
    
    // Reset states
    setIsConnected(false);
    setHasInitialized(false);
    setSelectedNodeMetadata(null);
    
    // Increment session ID to force fresh query
    setSessionId(prev => prev + 1);
  };

  const handleLayoutToggle = () => {
    setUseHierarchical(prev => !prev);
    
    // If we have a network, reinitialize with new layout
    if (networkRef.current && graphData) {
      setHasInitialized(false);
    }
  };

  const handleLegendToggle = () => {
    setShowLegend(prev => !prev);
  };

  const handleOpenNeo4jBrowser = () => {
    // Guarded by the buttons themselves (rendered only when neo4jBrowserUrl
    // is set), but keep an early-return in case a future caller invokes it
    // programmatically before checking the config.
    const browserUrl = config.neo4jBrowserUrl;
    if (!browserUrl) return;
    let cypherCommand: string;
    
    if (focusNodeId) {
      // Open with focused node query
      cypherCommand = `MATCH path = (target)-[*0..1]-(neighbor)\nWHERE target.id = "${focusNodeId}" OR id(target) = ${focusNodeId}\nRETURN path`;
    } else if (projectUuid) {
      // Open with project-specific query
      cypherCommand = `MATCH (p:Project {uuid: "${projectUuid}"})-[*0..4]-(n)\nRETURN p, n`;
    } else {
      // Fallback to general query
      cypherCommand = `MATCH (n) RETURN n LIMIT 25`;
    }
    
    const urlWithCommand = `${browserUrl}/browser/?cmd=edit&arg=${cypherCommand}`;
    window.open(urlWithCommand, '_blank', 'noopener,noreferrer');
  };

  // Repository list, from PostgreSQL. Small (single digits to low hundreds) and
  // already cached by the dashboard, so it is fetched with the page rather than
  // on expansion — the Code node then expands instantly.
  const {
    data: repoResponse,
    isLoading: isLoadingRepos,
    isError: isRepoError,
  } = useGetCodeOntologies(projectUuid || '', 1, 200);
  const repos: any[] = (repoResponse as any)?.data ?? [];


  // Lazy expansion. The root query loads only the project, its ontology roots
  // and one level under each; everything deeper arrives one click at a time and
  // is merged into the live DataSets so the camera and the rest of the graph
  // stay put.
  const { mutate: fetchChildren } = useExpandNodeChildren(
    (data, nodeId, truncated) => mergeIntoGraph(data, nodeId, truncated),
    (error) => {
      setExpandingNodeId(null);
      toast.error('Failed to expand node: ' + error.message);
    }
  );

  /**
   * Merge a fetched sub-graph into the live network. `parentIdForSynthEdges`
   * is supplied when the children have no edges of their own — repository files
   * are the case: nothing in Neo4j links a File back to its repo, so the edges
   * are drawn here.
   */
  const mergeIntoGraph = (
    data: any,
    nodeId: string,
    truncated: boolean,
    parentIdForSynthEdges?: string
  ) => {
    setExpandingNodeId(null);
    if (!nodesDataSetRef.current || !edgesDataSetRef.current) return;

    const incoming = data.nodes ?? [];
    const realChildren = parentIdForSynthEdges
      ? incoming
      : incoming.filter((n: any) => String(n.id) !== nodeId);

    if (realChildren.length === 0) {
      setExpandedNodeIds((prev) => new Set(prev).add(nodeId));
      toast.info('This node has no children.');
      return;
    }

    incoming.forEach((n: any) => {
      nodeDataRef.current.set(String(n.id), {
        id: String(n.id),
        labels: n.labels ?? [],
        properties: n.properties ?? {},
      });
    });

    const payload = { ...data };
    if (parentIdForSynthEdges) {
      payload.edges = [
        ...(data.edges ?? []),
        ...realChildren.map((n: any) => ({
          id: `${parentIdForSynthEdges}|${n.id}`,
          from: parentIdForSynthEdges,
          to: String(n.id),
          type: 'HAS_FILE',
          properties: {},
        })),
      ];
    }

    const { nodes: visNodes, edges: visEdges } = transformGraphData(payload, isLightTheme);
    nodesDataSetRef.current.update(visNodes as any[]);
    edgesDataSetRef.current.update(visEdges as any[]);

    setExpandedNodeIds((prev) => new Set(prev).add(nodeId));

    const added = realChildren.length;
    if (truncated) {
      toast.warning(`Showing the first ${added} children — this node has more.`);
    } else {
      toast.success(`Expanded ${added} node${added === 1 ? '' : 's'}`);
    }
  };

  const { mutate: fetchRepoFiles } = useGetRepoFiles(
    (data, repoId, truncated) => mergeIntoGraph(data, repoId, truncated, repoId),
    (error) => {
      setExpandingNodeId(null);
      toast.error('Failed to load repository files: ' + error.message);
    }
  );

  /**
   * Code -> repositories. A PostgreSQL list, so no Cypher is involved.
   *
   * `anchorId` is the id of whichever "Code" node the user clicked — the
   * legacy synthetic:code node for pre-BREEZEAI-533 projects, or the real
   * :Code node id for projects that have one. Edges and the "expanded" set
   * must key off that id, otherwise the repo nodes land on the canvas with
   * no edge back and the toggle never flips to "expanded".
   */
  const expandCodeRoot = (anchorId: string = CODE_ROOT_ID) => {
    if (!nodesDataSetRef.current || !edgesDataSetRef.current) return;

    // The repository list is a separate request from the graph, and the Code node
    // is clickable as soon as the graph draws. Clicking first used to read an
    // empty list, report "no repositories" and mark the node expanded for good —
    // so whichever project's list happened to be slower could never be explored.
    // Neither of these branches marks the node expanded: both are retryable.
    if (isLoadingRepos) {
      toast.info('Still loading repositories — try again in a moment.');
      return;
    }
    if (isRepoError) {
      toast.error('Could not load the repository list for this project.');
      return;
    }
    if (repos.length === 0) {
      toast.info('No repositories have been onboarded for this project.');
      return;
    }

    const repoNodes = repos.map((r) => ({
      id: repoNodeId(r._id),
      labels: ['Repository'],
      properties: {
        name: r.name,
        languages: Array.isArray(r.languages) ? r.languages.join(', ') : r.language,
        files: r.fileCount,
        functions: r.functionCount,
        classes: r.classCount,
        linesOfCode: r.totalLinesOfCode,
        status: r.status,
        source: `PostgreSQL row _id=${r._id}`,
      },
    }));

    repoNodes.forEach((n, i) => {
      nodeDataRef.current.set(n.id, {
        id: n.id,
        labels: n.labels,
        properties: n.properties,
        synthetic: 'repo',
        codeOntologyId: Number(repos[i]._id),
      });
    });

    const { nodes: visNodes, edges: visEdges } = transformGraphData(
      {
        nodes: repoNodes,
        edges: repoNodes.map((n) => ({
          id: `${anchorId}|${n.id}`,
          from: anchorId,
          to: n.id,
          type: 'HAS_REPOSITORY',
          properties: {},
        })),
      } as any,
      isLightTheme
    );

    nodesDataSetRef.current.update(visNodes as any[]);
    edgesDataSetRef.current.update(visEdges as any[]);
    setExpandedNodeIds((prev) => new Set(prev).add(anchorId));
    toast.success(`Expanded ${repoNodes.length} repositor${repoNodes.length === 1 ? 'y' : 'ies'}`);
  };

  /**
   * Design -> its user journeys. The journeys are already in memory, so they are
   * added directly.
   */
  const expandDesignRoot = (designId: string) => {
    if (!nodesDataSetRef.current || !edgesDataSetRef.current) return;

    if (journeys.length === 0) {
      // Not marked expanded: generating a design ontology should not require a
      // page reload before the branch can be opened.
      toast.info('No design ontology has been generated for this project yet.');
      return;
    }

    {
      journeys.forEach((j: any) => {
        nodeDataRef.current.set(String(j.id), {
          id: String(j.id),
          labels: j.labels ?? ['UserJourney'],
          properties: j.properties ?? {},
        });
      });

      const { nodes: visNodes, edges: visEdges } = transformGraphData(
        {
          nodes: journeys,
          edges: journeys.map((j: any) => ({
            id: `${designId}|${j.id}`,
            from: designId,
            to: String(j.id),
            type: 'HAS_USER_JOURNEY',
            properties: {},
          })),
        } as any,
        isLightTheme
      );

      nodesDataSetRef.current.update(visNodes as any[]);
      edgesDataSetRef.current.update(visEdges as any[]);
      toast.success(
        `Expanded ${journeys.length} user journey${journeys.length === 1 ? '' : 's'}`
      );
    }

    setExpandedNodeIds((prev) => new Set(prev).add(designId));
  };

  const handleExpandNode = (nodeId: string) => {
    if (!nodeId || expandingNodeId) return;

    const meta = nodeDataRef.current.get(nodeId);

    // The two UI-invented nodes resolve against PostgreSQL, not the graph.
    if (meta?.synthetic === 'code-root') {
      expandCodeRoot(nodeId);
      return;
    }
    if (meta?.synthetic === 'design-root') {
      expandDesignRoot(nodeId);
      return;
    }
    if (meta?.synthetic === 'repo') {
      setExpandingNodeId(nodeId);
      fetchRepoFiles({ codeOntologyId: meta.codeOntologyId!, repoNodeId: nodeId });
      return;
    }

    const label = meta?.labels?.[0];
    if (!label) {
      // Without a label the query cannot use an index and would scan the whole
      // database, so refuse rather than hang for the better part of a minute.
      toast.error('Cannot expand this node — its type is unknown.');
      return;
    }
    if (LABELS_WITHOUT_ID_INDEX.has(label)) {
      // Statement carries no range index on `id`, so an expansion from one would
      // scan every node. It is a leaf in the code ontology anyway.
      toast.info('This node has no children.');
      setExpandedNodeIds((prev) => new Set(prev).add(nodeId));
      return;
    }

    setExpandingNodeId(nodeId);
    fetchChildren({ nodeId, label });
  };

  // BREEZEAI-1328: pick the next node in the direction of the pressed arrow,
  // using the straight-line distance to candidate nodes whose vector from the
  // current one points mostly in that direction. Falls back to any node when
  // nothing lies in the half-plane (e.g. the user is at a corner of the graph).
  const pickNextNodeInDirection = (
    currentId: string | null,
    direction: 'left' | 'right' | 'up' | 'down',
  ): string | null => {
    const network = networkRef.current;
    if (!network) return null;
    const allIds = nodesDataSetRef.current?.getIds().map(String) ?? [];
    if (allIds.length === 0) return null;

    const positions = network.getPositions(allIds) as Record<string, { x: number; y: number }>;
    if (!currentId || !positions[currentId]) {
      return allIds[0] ?? null;
    }

    const origin = positions[currentId];
    const axisSign = direction === 'right' || direction === 'down' ? 1 : -1;
    const isHorizontal = direction === 'left' || direction === 'right';

    let bestId: string | null = null;
    let bestScore = Infinity;
    for (const id of allIds) {
      if (id === currentId) continue;
      const p = positions[id];
      const dx = p.x - origin.x;
      const dy = p.y - origin.y;
      const primary = isHorizontal ? dx : dy;
      const secondary = isHorizontal ? dy : dx;
      // Must lie on the correct side.
      if (primary * axisSign <= 0) continue;
      // Weight the off-axis distance more heavily so arrow navigation feels
      // directional, not just "nearest node that happens to be on this side".
      const score = Math.abs(primary) + Math.abs(secondary) * 2;
      if (score < bestScore) {
        bestScore = score;
        bestId = id;
      }
    }

    // Nothing in the chosen half-plane — fall back to the globally nearest
    // other node so the key still does something at the graph's edges.
    if (!bestId) {
      for (const id of allIds) {
        if (id === currentId) continue;
        const p = positions[id];
        const d = Math.hypot(p.x - origin.x, p.y - origin.y);
        if (d < bestScore) {
          bestScore = d;
          bestId = id;
        }
      }
    }
    return bestId;
  };

  const selectNodeFromKeyboard = (nodeId: string) => {
    const network = networkRef.current;
    if (!network) return;
    network.selectNodes([nodeId]);
    // Programmatic selectNodes does not fire the selectNode event, so update
    // the metadata panel directly — matches the behaviour of clicking a node.
    const meta = nodeDataRef.current.get(nodeId);
    if (meta) {
      setSelectedNodeMetadata({
        id: meta.id,
        labels: meta.labels,
        properties: meta.properties,
        nodeId,
      });
    }
    network.focus(nodeId, {
      scale: network.getScale(),
      animation: { duration: 200, easingFunction: 'easeInOutQuad' },
    });
  };

  const handleCanvasKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const key = event.key;
    const currentId = selectedNodeMetadata ? String(selectedNodeMetadata.id) : null;

    if (key === 'ArrowRight' || key === 'ArrowLeft' || key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      const direction =
        key === 'ArrowRight' ? 'right'
        : key === 'ArrowLeft' ? 'left'
        : key === 'ArrowUp' ? 'up'
        : 'down';
      const nextId = pickNextNodeInDirection(currentId, direction);
      if (nextId) selectNodeFromKeyboard(nextId);
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      // No node focused yet — Enter/Space picks the first one so the user has
      // somewhere to go.
      if (!currentId) {
        const firstId = pickNextNodeInDirection(null, 'right');
        if (firstId) selectNodeFromKeyboard(firstId);
      } else {
        // Already focused — re-select to ensure the Node Details panel is open.
        selectNodeFromKeyboard(currentId);
      }
    }
  };

  const handleClose = () => {
    // Clean up network instance
    if (networkRef.current) {
      networkRef.current.destroy();
      networkRef.current = null;
    }

    // Clear container
    if (vizContainerRef.current) {
      vizContainerRef.current.innerHTML = '';
    }

    // Reset states
    setIsConnected(false);
    setExpandedNodeIds(new Set());
    setExpandingNodeId(null);
    nodesDataSetRef.current = null;
    edgesDataSetRef.current = null;

    onClose();
  };

  return (
    <VisualizerShell
      asPage={asPage}
      isOpen={isOpen}
      onClose={handleClose}
      isLightTheme={isLightTheme}
    >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isLightTheme
              ? ''
              : 'bg-surface-card border-surface-border'
          }`}
          style={{
            backgroundColor: isLightTheme ? '#ffffff' : undefined,
            borderColor: isLightTheme ? '#e2e8f0' : undefined,
          }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Database className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${
                isLightTheme ? 'text-slate-900' : 'text-white'
              }`}>
                {focusNodeId ? 'Node Focus View' : 'Knowledge Graph'}
              </h2>
              <p className={`text-sm ${
                isLightTheme ? 'text-slate-600' : 'text-muted'
              }`}>
                {focusNodeId ? `Focused on node: ${focusNodeId}` : 'Ontology relationship visualization'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Connection Status */}
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                isConnected
                  ? "bg-success/20 text-success"
                  : error
                  ? "bg-error/20 text-error"
                  : isLightTheme
                  ? "bg-slate-100 text-muted"
                  : "bg-surface text-muted"
              }`}
              role="status"
              aria-live="polite"
              aria-label={`Graph status: ${isConnected ? "Connected" : error ? "Error" : (isLoading || isFetching) ? "Loading" : "Connecting"}`}
            >
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? "bg-success" : error ? "bg-error" : "bg-muted"
              }`} aria-hidden="true" />
              {isConnected ? "Connected" : error ? "Error" : (isLoading || isFetching) ? "Loading..." : "Connecting"}
            </div>

            {/* Actions */}
            {(isConnected || graphData) && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLegendToggle}
                  className={
                    isLightTheme
                      ? `text-slate-600 hover:text-slate-900 ${showLegend ? 'bg-slate-100 text-slate-900' : ''}`
                      : `text-muted hover:text-white ${showLegend ? 'bg-surface-hover text-white' : ''}`
                  }
                  title={`${showLegend ? 'Hide' : 'Show'} Legend`}
                  aria-label={`${showLegend ? 'Hide' : 'Show'} legend`}
                  aria-pressed={showLegend}
                >
                  {showLegend ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLayoutToggle}
                  className={
                    isLightTheme
                      ? `text-slate-600 hover:text-slate-900 ${useHierarchical ? 'bg-slate-100 text-slate-900' : ''}`
                      : `text-muted hover:text-white ${useHierarchical ? 'bg-surface-hover text-white' : ''}`
                  }
                  title={`Switch to ${useHierarchical ? 'Force-directed' : 'Hierarchical'} Layout`}
                  aria-label={`Switch to ${useHierarchical ? 'force-directed' : 'hierarchical'} layout`}
                  aria-pressed={useHierarchical}
                >
                  {useHierarchical ? <TreePine className="w-4 h-4" aria-hidden="true" /> : <NetworkIcon className="w-4 h-4" aria-hidden="true" />}
                </Button>
                {config.neo4jBrowserUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenNeo4jBrowser}
                    className={
                      isLightTheme
                        ? 'text-slate-600 hover:text-slate-900'
                        : 'text-muted hover:text-white'
                    }
                    title="Open in Neo4j Browser"
                    aria-label="Open graph in Neo4j Browser (opens in new tab)"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className={
                    isLightTheme
                      ? 'text-slate-600 hover:text-slate-900'
                      : 'text-muted hover:text-white'
                  }
                  disabled={isLoading}
                  title="Refresh Graph"
                  aria-label="Refresh graph data"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className={`custom-close-btn ${
                isLightTheme
                  ? 'text-slate-600 hover:text-slate-900'
                  : 'text-muted hover:text-white'
              }`}
              aria-label="Close graph visualizer"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Visualization Container */}
        <div
          className="relative flex-1 min-h-0 bg-background pt-3"
          role="application"
          aria-label="Knowledge graph visualization"
          aria-describedby="graph-instructions"
        >
          {/* First-run affordance. The opening view is deliberately just the
              ontology roots, so without this the graph reads as "that's all
              there is" rather than "this is the start". Disappears for good
              after the first expansion. */}
          {isConnected && expandedNodeIds.size === 0 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-full border shadow-lg backdrop-blur-sm text-xs font-medium ${
                  isLightTheme
                    ? 'bg-white/95 border-slate-200 text-slate-700'
                    : 'bg-surface-card/95 border-surface-border text-white'
                }`}
              >
                <Plus className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                Double-click any node to expand it
              </div>
            </div>
          )}

          {/* Legend Overlay */}
          {showLegend && isConnected && (
            <div
              className={`absolute top-4 right-4 z-20 backdrop-blur-sm border rounded-lg shadow-lg ${
                legendExpanded ? 'max-w-xs w-72 max-h-96' : 'w-auto'
              } ${
                isLightTheme
                  ? ''
                  : 'bg-surface-card/95 border-surface-border'
              }`}
              style={{
                backgroundColor: isLightTheme ? 'rgba(255, 255, 255, 0.95)' : undefined,
                borderColor: isLightTheme ? '#e2e8f0' : undefined,
              }}
            >
              {/* Legend Header — also the expand/collapse control */}
              <button
                onClick={() => setLegendExpanded((prev) => !prev)}
                aria-expanded={legendExpanded}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                  legendExpanded
                    ? `border-b ${isLightTheme ? 'border-slate-200' : 'border-surface-border'}`
                    : ''
                } ${isLightTheme ? 'hover:bg-slate-50' : 'hover:bg-surface-hover'}`}
              >
                <Info className="w-4 h-4 text-primary flex-shrink-0" />
                <h3 className={`text-xs font-semibold ${
                  isLightTheme ? 'text-slate-900' : 'text-button-text'
                }`}>Legend</h3>
                <div className="ml-auto flex items-center gap-2 text-xs text-muted">
                  {legendExpanded && (
                    <span>{LEGEND_GROUPS.find((g) => g.group === legendTab)?.entries.length ?? 0} types</span>
                  )}
                  {legendExpanded
                    ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                    : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
                </div>
              </button>

              {legendExpanded && (
                <>
              {/* Ontology tabs — 45 node types is far too many for one flat list */}
              <div
                className={`flex gap-1 px-2 pt-2 pb-1 overflow-x-auto border-b ${
                  isLightTheme ? 'border-slate-200' : 'border-surface-border'
                }`}
                role="tablist"
                aria-label="Legend categories"
              >
                {LEGEND_GROUPS.map(({ group }) => (
                  <button
                    key={group}
                    role="tab"
                    aria-selected={legendTab === group}
                    onClick={() => setLegendTab(group)}
                    className={`px-2 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-colors ${
                      legendTab === group
                        ? 'bg-primary text-white'
                        : isLightTheme
                        ? 'text-slate-600 hover:bg-slate-100'
                        : 'text-muted hover:bg-surface-hover'
                    }`}
                  >
                    {group}
                  </button>
                ))}
              </div>

              {/* Scrollable Legend Content */}
              <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-track-surface scrollbar-thumb-surface-border hover:scrollbar-thumb-surface-hover">
                <div className="p-4 space-y-2">
                  {(LEGEND_GROUPS.find((g) => g.group === legendTab)?.entries ?? []).map((legend) => (
                    <div key={legend.type} className="flex items-center gap-3 py-1">
                      <LegendSwatch shape={legend.shape} color={legend.color} border={legend.border} />
                      <div className="min-w-0 flex-1">
                        <div className={`text-xs font-medium truncate ${
                          isLightTheme ? 'text-slate-900' : 'text-button-text'
                        }`}>
                          {legend.type}
                        </div>
                        <div className="text-xs text-muted truncate">
                          {legend.description}
                        </div>
                      </div>
                      <div className="text-xs text-muted/70 font-mono">
                        L{legend.hierarchy}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

                </>
              )}

              {/* Legend Footer */}
              {legendExpanded && (
              <div className={`px-4 py-2 border-t ${
                isLightTheme
                  ? 'border-slate-200 bg-slate-50/50'
                  : 'border-surface-border bg-surface/50'
              }`}>
                <div className="text-xs text-muted">
                  Hierarchy: L0 (root) → L6 (leaf)
                </div>
              </div>
              )}
            </div>
          )}

          {/* Node Metadata Panel */}
          {selectedNodeMetadata && (
            <div
              className={`absolute top-4 left-4 z-30 border rounded-lg shadow-2xl max-w-sm w-80 ${
                isLightTheme
                  ? ''
                  : 'bg-surface-card border-surface-border'
              }`}
              style={{
                backdropFilter: 'blur(8px)',
                backgroundColor: isLightTheme ? '#ffffff' : undefined,
                borderColor: isLightTheme ? '#e2e8f0' : undefined,
              }}
            >
              {/* Metadata Header */}
              <div
                className={`flex items-center gap-2 p-4 pb-3 border-b ${
                  isLightTheme ? '' : 'border-surface-border'
                }`}
                style={{
                  borderColor: isLightTheme ? '#e2e8f0' : undefined,
                }}
              >
                <FileText className="w-4 h-4 text-primary" />
                <h3 className={`text-sm font-semibold ${
                  isLightTheme ? 'text-slate-900' : 'text-white'
                }`}>Node Details</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedNodeMetadata(null)}
                  className={`ml-auto w-6 h-6 p-0 ${
                    isLightTheme
                      ? 'text-slate-600 hover:text-slate-900'
                      : 'text-muted hover:text-white'
                  }`}
                  aria-label="Close node details panel"
                >
                  <X className="w-3 h-3" aria-hidden="true" />
                </Button>
              </div>

              {/* Metadata Content */}
              <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-track-surface scrollbar-thumb-surface-border hover:scrollbar-thumb-surface-hover">
                <div className="p-4 space-y-4">
                  {/* Node ID */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Hash className={`w-3 h-3 ${isLightTheme ? 'text-slate-500' : 'text-muted'}`} />
                      <span className={`text-xs font-medium uppercase tracking-wider ${
                        isLightTheme ? 'text-slate-500' : 'text-muted'
                      }`}>Node ID</span>
                    </div>
                    <div className={`text-sm font-mono px-2 py-1 rounded ${
                      isLightTheme
                        ? 'text-slate-900 bg-slate-50'
                        : 'text-white bg-surface/50'
                    }`}>
                      {selectedNodeMetadata.id}
                    </div>
                  </div>

                  {/* Labels */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Tag className={`w-3 h-3 ${isLightTheme ? 'text-slate-500' : 'text-muted'}`} />
                      <span className={`text-xs font-medium uppercase tracking-wider ${
                        isLightTheme ? 'text-slate-500' : 'text-muted'
                      }`}>Labels</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedNodeMetadata.labels.map((label: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full border border-primary/30"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Expand one level below this node */}
                  <div className="space-y-2">
                    <Button
                      onClick={() => handleExpandNode(String(selectedNodeMetadata.id))}
                      disabled={
                        expandingNodeId !== null ||
                        expandedNodeIds.has(String(selectedNodeMetadata.id))
                      }
                      className="w-full bg-primary hover:bg-primary-dark text-white text-xs h-8 disabled:opacity-50"
                    >
                      {expandingNodeId === String(selectedNodeMetadata.id) ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                          Expanding...
                        </>
                      ) : expandedNodeIds.has(String(selectedNodeMetadata.id)) ? (
                        'Expanded'
                      ) : (
                        <>
                          <Plus className="w-3 h-3 mr-2" />
                          {(() => {
                            // Known only for the ontology roots and the synthetic
                            // Code node; everything deeper reports its count by
                            // being expanded.
                            const id = String(selectedNodeMetadata.id);
                            const synthetic = nodeDataRef.current.get(id)?.synthetic;
                            const known =
                              synthetic === 'code-root'
                                ? repos.length
                                : synthetic === 'design-root'
                                ? journeys.length + (rootChildCountsRef.current.get(id) ?? 0)
                                : rootChildCountsRef.current.get(id);
                            return known === undefined
                              ? 'Expand children'
                              : `Expand · ${known} child${known === 1 ? '' : 'ren'}`;
                          })()}
                        </>
                      )}
                    </Button>
                    <p className={`text-[11px] ${isLightTheme ? 'text-slate-500' : 'text-muted'}`}>
                      Or double-click the node on the canvas.
                    </p>
                  </div>

                  {/* Properties */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Info className={`w-3 h-3 ${isLightTheme ? 'text-slate-500' : 'text-muted'}`} />
                      <span className={`text-xs font-medium uppercase tracking-wider ${
                        isLightTheme ? 'text-slate-500' : 'text-muted'
                      }`}>Properties</span>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(selectedNodeMetadata.properties).length > 0 ? (
                        Object.entries(selectedNodeMetadata.properties).map(([key, value]) => (
                          <div key={key} className="space-y-1">
                            <div className={`text-xs font-medium ${
                              isLightTheme ? 'text-slate-900' : 'text-white'
                            }`}>{key}</div>
                            <div className={`text-xs px-2 py-1 rounded leading-relaxed ${
                              isLightTheme
                                ? 'text-slate-600 bg-slate-50'
                                : 'text-muted bg-surface/50'
                            }`}>
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className={`text-xs italic ${
                          isLightTheme ? 'text-slate-400' : 'text-muted'
                        }`}>No properties available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className={`text-center space-y-3 p-6 rounded-lg border ${
                isLightTheme
                  ? 'bg-white border-slate-200'
                  : 'bg-surface-card border-surface-border'
              }`}>
                <Database className="w-12 h-12 text-error mx-auto" />
                <div>
                  <h3 className={`font-medium ${
                    isLightTheme ? 'text-slate-900' : 'text-white'
                  }`}>Failed to Load Graph</h3>
                  <p className="text-muted text-sm mt-1">{error}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRefresh}
                    className={`text-white ${
                      isLightTheme
                        ? 'bg-primary hover:bg-primary-dark'
                        : 'bg-primary hover:bg-primary-dark'
                    }`}
                  >
                    Retry
                  </Button>
                  {config.neo4jBrowserUrl && (
                    <Button
                      onClick={handleOpenNeo4jBrowser}
                      variant="outline"
                      className={
                        isLightTheme
                          ? 'border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                          : 'border-surface-border text-muted hover:text-white hover:bg-surface-hover'
                      }
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in Neo4j Browser
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {(isLoading || isFetching) && !error && (
            <div className={`absolute inset-0 flex items-center justify-center z-10 ${
              isLightTheme ? 'bg-slate-50/80' : 'bg-background/80'
            }`}>
              <div className="text-center space-y-3">
                <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto" />
                <p className={isLightTheme ? 'text-slate-900' : 'text-white'}>Loading knowledge graph...</p>
              </div>
            </div>
          )}

          <div
            ref={vizContainerRef}
            id="knowledge-graph-canvas"
            className="w-full h-full focus:outline-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-3px] focus-visible:outline-primary"
            tabIndex={0}
            role="img"
            aria-label={`Interactive knowledge graph with ${graphData?.nodes?.length || 0} nodes. Use arrow keys to move between nodes, Enter or Space to open node details, double-click to expand.`}
            aria-describedby="graph-instructions"
            onKeyDown={handleCanvasKeyDown}
            style={{
              background: isLightTheme
                ? 'radial-gradient(circle, #f8fafc 0%, #e2e8f0 100%)'
                : 'radial-gradient(circle, #1a1a1a 0%, #000000 100%)',
            }}
          />
        </div>

        {/* The instruction bar that used to sit here restated things the UI already
            shows — the layout and legend toggles are visible buttons, and pan/zoom
            /hover are how every graph works. It also leaked the project UUID. The
            one instruction that is NOT discoverable, double-click to expand, is
            surfaced on the canvas instead and repeated on the panel button.
            Kept here for screen readers, which have no other route to it. */}
        <p id="graph-instructions" className="sr-only">
          Interactive knowledge graph. Drag to pan, scroll to zoom, click a node for
          its details, and double-click a node to load its children. Keyboard: Tab
          to focus the graph, then arrow keys to move between nodes, Enter or Space
          to open the node details panel.
        </p>
    </VisualizerShell>
  );
}